import { ImageIcon, AlertCircle, Loader2, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useGetEvidenceReadUrl } from '@/features/orders/orders.queries';

interface EvidenceThumbnailProps {
  gcs_path: string | null | undefined;
}

/**
 * Renders a clickable thumbnail for an evidence file stored in GCS.
 * Uses TanStack Query to fetch and cache the signed read URL.
 * Opens a modal lightbox on click instead of navigating away.
 */
export function EvidenceThumbnail(props: EvidenceThumbnailProps) {
  const { gcs_path } = props;
  const [is_modal_open, setIsModalOpen] = useState(false);

  const { data, isLoading, isError } = useGetEvidenceReadUrl(gcs_path);

  useEffect(() => {
    if (!is_modal_open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsModalOpen(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [is_modal_open]);

  if (!gcs_path) return null;

  if (isLoading) {
    return (
      <div className="flex h-20 w-20 items-center justify-center rounded-lg border border-base-300 bg-base-200">
        <Loader2 className="h-5 w-5 animate-spin opacity-20" />
      </div>
    );
  }

  if (isError || !data?.read_url) {
    return (
      <div
        className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-lg border border-error/20 bg-error/5 text-[10px] text-error"
        title="No se pudo cargar la evidencia"
      >
        <AlertCircle className="h-5 w-5" />
        <span className="text-center px-1 font-medium">Error</span>
      </div>
    );
  }

  const onOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsModalOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={onOpen}
        className="group relative block h-20 w-20 overflow-hidden rounded-lg border border-base-300 bg-base-200 transition-transform hover:scale-105 active:scale-95"
      >
        <img
          src={data.read_url}
          alt="Evidencia"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
          <ImageIcon className="h-5 w-5 text-white" />
        </div>
      </button>

      {/* Lightbox Modal */}
      {is_modal_open && (
        <dialog className="modal modal-open z-[10000]">
          <div className="modal-box p-0 max-w-4xl bg-transparent shadow-none">
            <div className="relative flex flex-col items-center">
              <button
                className="btn btn-sm btn-circle btn-neutral absolute -top-12 right-0 text-white"
                onClick={() => setIsModalOpen(false)}
              >
                <X size={20} />
              </button>
              <img
                src={data.read_url}
                alt="Evidencia ampliada"
                className="max-h-[80vh] w-auto rounded-lg shadow-2xl"
              />
            </div>
          </div>
          <div className="modal-backdrop bg-black/80 backdrop-blur-sm" onClick={() => setIsModalOpen(false)} />
        </dialog>
      )}
    </>
  );
}
